import { Check, Circle, Loader2, RefreshCw, Square, TriangleAlert, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Banner } from "../../components/Callout";
import { Badge, Button, IconButton } from "../../components/ui";
import { Waiting } from "../../components/Waiting";
import { locale, t, tServer } from "../../i18n/i18n";
import { aiSourceKey } from "../../panels/aiSource";
import type { DocumentJob, JobStep } from "./documentJob";

/** Pièces communes aux documents rédigés par l'IA locale (mise en ambiance, fiche pays) : bandeau de la rédaction
 *  et état du modèle dans l'en-tête de la vue. */

/** Textes du bandeau propres au document, déjà traduits. */
export interface ProgressTexts {
  /** Une section demandée, plusieurs, ou tout le document, rédigés en {duration}. */
  one: (duration: string) => string;
  some: (duration: string) => string;
  all: (duration: string) => string;
  /** Rédaction arrêtée par l'utilisateur. */
  stopped: string;
}

/** État d'une section dans le bandeau : seulement l'avancement — à venir, en cours, faite. Une section dont la
 *  réponse de l'IA a été écartée (texte de Generex gardé, état `fallback` du serveur) est faite, comme les autres :
 *  aucune alerte ni aucun « refus » dans le bandeau (demande d'Alexis du 13/10/2026). */
export function stepProgress(state: JobStep["state"]): "pending" | "running" | "done" {
  return state === "pending" ? "pending" : state === "running" ? "running" : "done";
}

/** Rédaction en cours ou achevée : bandeau sous l'en-tête (`Banner`), étapes en pastilles (à venir, en cours, faite),
 *  arrêt et reprise. Seul un échec de la rédaction elle-même (dossier incomplet, serveur muet) l'alerte. */
export function ProgressStrip({
  job,
  texts,
  onStop,
  onRetry,
  onDismiss,
}: {
  job: DocumentJob<unknown>;
  texts: ProgressTexts;
  onStop: () => void;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  const [now, setNow] = useState(() => performance.now());
  useEffect(() => {
    if (!job.running) return;
    const timer = window.setInterval(() => setNow(performance.now()), 500);
    return () => window.clearInterval(timer);
  }, [job.running]);
  // Rédaction achevée : le bandeau se retire de lui-même.
  useEffect(() => {
    if (job.running || job.error || job.stopped) return;
    const timer = window.setTimeout(onDismiss, 6000);
    return () => window.clearTimeout(timer);
  }, [job.running, job.error, job.stopped, onDismiss]);
  const seconds = Math.max(0, Math.round((now - job.startedAt) / 1000));
  const took = duration(job.elapsed ?? seconds);
  const current = job.steps.find((s) => s.state === "running");
  return (
    <Banner
      tone={job.error ? "danger" : "neutral"}
      icon={false}
      actions={
        job.running ? (
          <Button size="sm" variant="secondary" onClick={onStop}>
            <Square size={12} /> {t("Arrêter")}
          </Button>
        ) : (
          <>
            {(job.error || job.stopped) && (
              // Arrêt ou échec : la reprise est l'action attendue (bouton qui pulse).
              <Button size="sm" variant="secondary" onClick={onRetry} highlight>
                <RefreshCw size={14} /> {t("Reprendre")}
              </Button>
            )}
            <IconButton size="xs" onClick={onDismiss} title={t("Fermer")} aria-label={t("Fermer")}>
              <X size={14} />
            </IconButton>
          </>
        )
      }
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {job.running ? (
          <>
            <Loader2 size={14} className="animate-spin text-run" />
            <span className="font-medium text-ink">
              <Waiting>{current ? `${tServer(current.label)}…` : t("Préparation…")}</Waiting>
            </span>
            <span className="text-ink-muted tabular-nums">{seconds} s</span>
          </>
        ) : job.error ? (
          <>
            <TriangleAlert size={14} />
            <span className="font-medium">{tServer(job.error)}</span>
          </>
        ) : job.stopped ? (
          <>
            <Square size={12} />
            <span className="font-medium">{texts.stopped}</span>
          </>
        ) : (
          <>
            <Check size={14} className="text-ok" />
            <span className="font-medium text-ink">
              {job.targets?.length === 1 ? texts.one(took) : job.targets ? texts.some(took) : texts.all(took)}
            </span>
            {job.source && <span className="text-ink-muted">· {tServer(job.source)}</span>}
          </>
        )}
        <ol className="flex flex-wrap items-center gap-1.5">
          {job.steps
            .filter((s) => s.id !== "dossier")
            .map((s) => {
              const progress = stepProgress(s.state);
              return (
                <li key={s.id} className="flex">
                  <Badge
                    tone={progress === "running" ? "run" : progress === "done" ? "ok" : "neutral"}
                    title={progress === "pending" ? t("Étape à venir") : undefined}
                    className="gap-1"
                  >
                    {progress === "running" ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : progress === "done" ? (
                      <Check size={12} />
                    ) : (
                      <Circle size={10} aria-hidden />
                    )}
                    {tServer(s.label.replace(" — rédaction par l'IA", ""))}
                  </Badge>
                </li>
              );
            })}
        </ol>
      </div>
    </Banner>
  );
}

export function duration(seconds: number): string {
  return seconds < 10
    ? `${seconds.toLocaleString(locale(), { maximumFractionDigits: 1, minimumFractionDigits: 1 })} s`
    : `${Math.round(seconds)} s`;
}

/** État du modèle d'IA (serveur GPU, ou IA de secours) : pastille d'état (`Badge dot`) dans l'en-tête, qui nomme
 *  le rôle du modèle sans machine ni modèle (R15). `offline` : infobulle quand il ne répond pas (ce que le document
 *  fait alors). */
export function AiChip({
  available,
  source,
  offline,
  resuming,
}: {
  available?: boolean;
  source?: string;
  offline?: string;
  /** GPU en échec et IA de secours en démarrage (registre E29) : « Reprise de l'IA… », points animés. */
  resuming?: boolean;
}) {
  if (available === undefined) return null;
  return available ? (
    <Badge dot tone="run" title={t("Modèle joignable : {source}", { source: t(aiSourceKey(source ?? "")) })}>
      {t("IA prête")}
    </Badge>
  ) : resuming ? (
    <Badge dot tone="warn" title={t("Le serveur GPU ne répond pas : l'IA de secours démarre, elle sera prête dans une minute environ.")}>
      <Waiting>{t("Reprise de l'IA…")}</Waiting>
    </Badge>
  ) : (
    <Badge dot tone="warn" title={offline ?? t("L'IA ne répond pas : les paragraphes de l'IA reprendront leur texte source.")}>
      {t("IA hors ligne")}
    </Badge>
  );
}
